/**
 * Lucent Carat Lab - Exact Luxumoon PDP Controller
 * Direct synchronization with actual Shopify variants & options
 */

(function () {
  'use strict';

  function initLuxumoonPDP() {
    // 1. DYNAMIC DELIVERY DATE ESTIMATION (15 - 21 business days)
    const deliveryEl = document.querySelector('[data-lux-delivery-dates]');
    if (deliveryEl) {
      const today = new Date();
      const addDays = (date, days) => {
        const result = new Date(date);
        let count = 0;
        while (count < days) {
          result.setDate(result.getDate() + 1);
          if (result.getDay() !== 0 && result.getDay() !== 6) {
            count++;
          }
        }
        return result;
      };

      const start = addDays(today, 14);
      const end = addDays(today, 20);

      const months = [
        'January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December'
      ];

      const startFormatted = `${months[start.getMonth()]} ${start.getDate()}`;
      const endFormatted = start.getMonth() === end.getMonth()
        ? `${end.getDate()}`
        : `${months[end.getMonth()]} ${end.getDate()}`;

      deliveryEl.textContent = `${startFormatted} – ${endFormatted}`;
    }

    // 2. READ MORE / READ LESS TOGGLE
    const readMoreBtn = document.querySelector('[data-lux-read-more-btn]');
    const descText = document.querySelector('[data-lux-desc-text]');
    if (readMoreBtn && descText) {
      readMoreBtn.addEventListener('click', (e) => {
        e.preventDefault();
        const isExpanded = descText.classList.toggle('is-expanded');
        readMoreBtn.textContent = isExpanded ? 'Read less' : 'Read more';
      });
    }

    // 3. DYNAMIC SWATCH SELECTION & VARIANT SYNCHRONIZATION
    const swatchCards = document.querySelectorAll('[data-lux-swatch-card]');
    const metalLabel = document.querySelector('[data-selected-metal-name]');
    const moreBtn = document.querySelector('[data-lux-more-metals-btn]');
    const extraCards = document.querySelectorAll('.lux-pdp-swatch-card.is-extra-item');

    if (moreBtn && extraCards.length > 0) {
      moreBtn.addEventListener('click', (e) => {
        e.preventDefault();
        const isRevealed = extraCards[0].classList.contains('is-revealed');
        extraCards.forEach((c) => c.classList.toggle('is-revealed', !isRevealed));
        const icon = moreBtn.querySelector('.lux-pdp-swatch-more-icon');
        if (icon) {
          icon.textContent = isRevealed ? '+' : '−';
        }
      });
    }

    // Media classifier to group images and videos by metal
    function getMediaMetalGroup(el) {
      if (!el) return 'other';
      const alt = (el.getAttribute('data-media-alt') || el.querySelector('img')?.alt || '').toLowerCase();
      const src = (el.querySelector('img')?.src || '').toLowerCase();
      const text = alt + ' ' + src;

      if (text.includes('lifestyle') || text.includes('on-hand') || text.includes('model') || text.includes('editorial')) {
        return 'general';
      }
      if (text.includes('two-tone') || text.includes('two tone') || text.includes('twotone')) {
        if (text.includes('rose') || text.includes('- r -') || text.includes('_r_')) return 'rose';
        if (text.includes('yellow') || text.includes('- y -') || text.includes('_y_')) return 'yellow';
        return 'two-tone';
      }
      if (text.includes('rose') || text.includes('pink') || text.includes('- r -') || text.includes('_r_') || text.includes('/rose/')) {
        return 'rose';
      }
      if (text.includes('yellow') || text.includes('- y -') || text.includes('_y_') || text.includes('/yellow/')) {
        return 'yellow';
      }
      if (text.includes('platinum') || text.includes('/platinum/')) {
        return 'platinum';
      }
      if (text.includes('white') || text.includes('silver') || text.includes('- w -') || text.includes('_w_') || text.includes('/white/')) {
        return 'white';
      }
      if (el.getAttribute('data-media-type') === 'video') {
        return 'general';
      }
      return 'other';
    }

    function getTargetMetalGroup(metalVal) {
      const m = (metalVal || '').toLowerCase();
      if (m.includes('rose') || m.includes('pink')) return 'rose';
      if (m.includes('yellow') || (m.includes('gold') && !m.includes('white') && !m.includes('rose'))) return 'yellow';
      if (m.includes('platinum') || m.includes('plat')) return 'platinum';
      if (m.includes('white') || m.includes('silver')) return 'white';
      return '';
    }

    // Switch and filter PDP media gallery so only the selected metal's photos are visible
    function switchPdpImageForMetal(metalVal, preferredMediaId) {
      if (!metalVal && !preferredMediaId) return;

      const mediaGallery = document.querySelector('media-gallery');
      if (!mediaGallery) return;

      const slides = Array.from(mediaGallery.querySelectorAll('ul.product__media-list > li[data-media-id]'));
      if (!slides || slides.length === 0) return;

      const thumbnails = Array.from(mediaGallery.querySelectorAll('.thumbnail-list__item'));
      let targetGroup = getTargetMetalGroup(metalVal);

      // If platinum selected but no platinum-tagged photos exist, fallback to white (shared silver tone)
      if (targetGroup === 'platinum') {
        const hasPlat = slides.some((s) => getMediaMetalGroup(s) === 'platinum');
        if (!hasPlat) {
          targetGroup = 'white';
        }
      } else if (targetGroup === 'white') {
        const hasWhite = slides.some((s) => getMediaMetalGroup(s) === 'white');
        if (!hasWhite) {
          targetGroup = 'platinum';
        }
      }

      // 1. FILTER SLIDES & THUMBNAILS BY METAL
      if (targetGroup) {
        const hasMatchingSlides = slides.some((s) => getMediaMetalGroup(s) === targetGroup);
        if (hasMatchingSlides) {
          let visibleSlideCount = 0;
          slides.forEach((s) => {
            const grp = getMediaMetalGroup(s);
            const isVisible = (grp === targetGroup || grp === 'general');
            if (isVisible) {
              s.style.removeProperty('display');
              s.classList.remove('lux-media-hidden');
              visibleSlideCount++;
            } else {
              s.style.setProperty('display', 'none', 'important');
              s.classList.add('lux-media-hidden');
            }
          });

          thumbnails.forEach((t) => {
            const grp = getMediaMetalGroup(t);
            const isVisible = (grp === targetGroup || grp === 'general');
            if (isVisible) {
              t.style.removeProperty('display');
              t.classList.remove('lux-media-hidden');
            } else {
              t.style.setProperty('display', 'none', 'important');
              t.classList.add('lux-media-hidden');
            }
          });

          // Update gallery counter
          const totalCounter = mediaGallery.querySelector('.slider-counter--total');
          if (totalCounter && visibleSlideCount > 0) {
            totalCounter.textContent = String(visibleSlideCount);
          }
        }
      }

      // 2. FIND ACTIVE TARGET SLIDE AMONG VISIBLE SLIDES
      const visibleSlides = slides.filter((s) => !s.classList.contains('lux-media-hidden') && s.style.display !== 'none');
      let targetSlide = null;

      if (preferredMediaId) {
        targetSlide = visibleSlides.find((s) => {
          const sId = s.getAttribute('data-media-id') || '';
          return sId === String(preferredMediaId) || sId.endsWith(`-${preferredMediaId}`) || sId.includes(String(preferredMediaId));
        });
      }

      if (!targetSlide && visibleSlides.length > 0) {
        targetSlide = visibleSlides[0];
      }

      if (targetSlide) {
        const mediaId = targetSlide.getAttribute('data-media-id');

        // Call Dawn's setActiveMedia
        if (typeof mediaGallery.setActiveMedia === 'function') {
          mediaGallery.setActiveMedia(mediaId, false);
        }

        // Direct DOM active & smooth scroll
        slides.forEach((s) => s.classList.remove('is-active'));
        targetSlide.classList.add('is-active');

        const sliderList = targetSlide.parentElement;
        if (sliderList) {
          sliderList.scrollTo({ left: targetSlide.offsetLeft, behavior: 'smooth' });
        }

        // Update thumbnails
        if (thumbnails.length > 0) {
          thumbnails.forEach((t) => {
            const btn = t.querySelector('button.thumbnail');
            const isMatch = t.getAttribute('data-target') === mediaId;
            t.classList.toggle('is-active', isMatch);
            if (btn) btn.setAttribute('aria-current', isMatch ? 'true' : 'false');
            if (isMatch && typeof mediaGallery.setActiveThumbnail === 'function') {
              mediaGallery.setActiveThumbnail(t);
            }
          });
        }
      }
    }

    // 3. 2-ROW LUXURY JEWELRY CUSTOMIZER (SEPARATED METAL & GOLD PURITY)
    const customizerEl = document.querySelector('[data-lux-customizer]');
    const metalBtns = document.querySelectorAll('.lux-metal-btn');
    const purityBtns = document.querySelectorAll('.lux-purity-btn');
    const metalDisplay = document.querySelector('[data-selected-metal-name]');
    const purityDisplay = document.querySelector('[data-selected-purity-name]');
    const purityBoxes = document.querySelector('[data-lux-purity-boxes]');
    const purityNaMsg = document.querySelector('[data-lux-purity-na]');

    let currentMetal = document.querySelector('.lux-metal-btn.is-active')?.getAttribute('data-metal') || 'White Gold';
    let currentPurity = document.querySelector('.lux-purity-btn.is-active')?.getAttribute('data-purity') || '10K';

    function updateCustomizerUI() {
      // 1. Update metal buttons active state
      metalBtns.forEach((btn) => {
        const m = btn.getAttribute('data-metal');
        btn.classList.toggle('is-active', m === currentMetal);
      });
      if (metalDisplay) metalDisplay.textContent = currentMetal;

      // 2. Check if Non-Gold (Platinum or Silver)
      const isNonGold = (currentMetal === 'Platinum' || currentMetal === 'Silver');

      if (isNonGold) {
        if (purityBoxes) purityBoxes.style.display = 'none';
        if (purityNaMsg) {
          purityNaMsg.style.display = 'block';
          purityNaMsg.innerHTML = currentMetal === 'Platinum'
            ? '<span class="lux-purity-na-badge"><svg width="11" height="11" viewBox="0 0 24 24" fill="currentColor" style="vertical-align:middle;margin-right:4px;"><path d="M12 2l2.4 7.2L22 12l-7.6 2.8L12 22l-2.4-7.2L2 12l7.6-2.8z"/></svg>Solid 950 Platinum &bull; Hypoallergenic &amp; Naturally Luminous</span>'
            : '<span class="lux-purity-na-badge"><svg width="11" height="11" viewBox="0 0 24 24" fill="currentColor" style="vertical-align:middle;margin-right:4px;"><path d="M12 2l2.4 7.2L22 12l-7.6 2.8L12 22l-2.4-7.2L2 12l7.6-2.8z"/></svg>Fine 925 Sterling Silver &bull; Tarnish-Resistant Coating</span>';
        }
        if (purityDisplay) {
          purityDisplay.textContent = currentMetal === 'Platinum' ? '950 Pure' : '925 Silver';
        }
        ensurePropertyInput('Gold Purity', currentMetal === 'Platinum' ? '950 Pure' : '925 Silver');
      } else {
        if (purityBoxes) purityBoxes.style.display = 'flex';
        if (purityNaMsg) purityNaMsg.style.display = 'none';

        // Check which purities exist for currentMetal in allVariants
        if (allVariants && allVariants.length > 0) {
          const colorTerm = currentMetal.toLowerCase().includes('yellow') ? 'yellow' : (currentMetal.toLowerCase().includes('rose') ? 'rose' : 'white');
          const availablePurities = [];
          allVariants.forEach((v) => {
            const vTitle = (v.title || '').toLowerCase();
            const vOpts = (v.options || []).map((o) => (o || '').toLowerCase());
            if (vTitle.includes(colorTerm) || vOpts.some((o) => o.includes(colorTerm))) {
              if (vTitle.includes('10k') || vOpts.some((o) => o.includes('10k'))) availablePurities.push('10K');
              if (vTitle.includes('14k') || vOpts.some((o) => o.includes('14k'))) availablePurities.push('14K');
              if (vTitle.includes('18k') || vOpts.some((o) => o.includes('18k'))) availablePurities.push('18K');
            }
          });

          if (availablePurities.length > 0 && !availablePurities.includes(currentPurity)) {
            currentPurity = availablePurities[0];
          }

          purityBtns.forEach((btn) => {
            const p = btn.getAttribute('data-purity');
            const isAvail = availablePurities.length === 0 || availablePurities.includes(p);
            btn.style.display = isAvail ? '' : 'none';
          });
        }

        if (purityDisplay) purityDisplay.textContent = currentPurity;

        purityBtns.forEach((btn) => {
          const p = btn.getAttribute('data-purity');
          btn.classList.toggle('is-active', p === currentPurity);
        });
        ensurePropertyInput('Gold Purity', currentPurity);
      }

      ensurePropertyInput('Metal', currentMetal);

      // 3. Update "Know your ring" metal composition box
      const specTitle = isNonGold ? currentMetal : `${currentPurity} ${currentMetal}`;
      updateMetalSpecCard(specTitle);

      // 4. Switch & filter gallery media
      switchPdpImageForMetal(currentMetal);

      // 5. Match Shopify variant & sync pricing & form inputs
      if (typeof applyCustomizerToVariant === 'function') {
        applyCustomizerToVariant();
      }
    }

    metalBtns.forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        const m = btn.getAttribute('data-metal');
        if (!m) return;
        currentMetal = m;
        updateCustomizerUI();
      });
    });

    purityBtns.forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        const p = btn.getAttribute('data-purity');
        if (!p) return;
        currentPurity = p;
        if (currentMetal === 'Platinum' || currentMetal === 'Silver') {
          currentMetal = 'White Gold';
        }
        updateCustomizerUI();
      });
    });

    // Also support fallback swatch cards if ever present
    swatchCards.forEach((card) => {
      card.addEventListener('click', () => {
        const metalVal = card.getAttribute('data-metal-val') || '';
        if (metalVal.toLowerCase().includes('yellow')) currentMetal = 'Yellow Gold';
        else if (metalVal.toLowerCase().includes('rose')) currentMetal = 'Rose Gold';
        else if (metalVal.toLowerCase().includes('plat')) currentMetal = 'Platinum';
        else if (metalVal.toLowerCase().includes('silver')) currentMetal = 'Silver';
        else currentMetal = 'White Gold';

        if (metalVal.toLowerCase().includes('14k')) currentPurity = '14K';
        else if (metalVal.toLowerCase().includes('18k')) currentPurity = '18K';
        else if (metalVal.toLowerCase().includes('10k')) currentPurity = '10K';

        updateCustomizerUI();
      });
    });

    // Also listen to Dawn's native option inputs (e.g. standard radio buttons or dropdowns)
    document.addEventListener('change', (e) => {
      const target = e.target;
      if (!target || !target.matches('input[type="radio"], select')) return;
      const fieldset = target.closest('fieldset.product-form__input') || target.closest('.product-form__input');
      if (!fieldset) return;
      const legendText = (fieldset.querySelector('legend, label')?.textContent || '').toLowerCase();
      if (legendText.includes('metal') || legendText.includes('gold') || legendText.includes('color') || legendText.includes('colour')) {
        const metalVal = target.value;
        if (metalVal) {
          switchPdpImageForMetal(metalVal, null);
        }
      }
    });

    // Initial load: detect active metal and apply customizer state immediately
    setTimeout(() => {
      updateCustomizerUI();
    }, 60);

    // Re-filter whenever Dawn variants change or HTML is re-rendered
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.variantChange) {
      subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
        const currentCard = document.querySelector('.lux-pdp-swatch-card.is-selected');
        const activeMetal = currentCard?.getAttribute('data-metal-val');
        const featMediaId = event?.data?.variant?.featured_media?.id || currentCard?.getAttribute('data-media-id');
        if (activeMetal) {
          setTimeout(() => switchPdpImageForMetal(activeMetal, featMediaId), 60);
        }
      });
    }

    // 4. RING SIZE & CARAT CUSTOM DROPDOWNS & SYNC
    document.querySelectorAll('[data-lux-custom-select-wrap]').forEach((wrap) => {
      const trigger = wrap.querySelector('[data-lux-custom-select-trigger]');
      const nativeSelect = wrap.querySelector('[data-lux-size-select], [data-lux-carat-select], select');
      const dropdown = wrap.querySelector('[data-lux-custom-select-list]');
      const items = wrap.querySelectorAll('.lux-custom-select-item');
      const curSize = wrap.querySelector('[data-lux-select-current-size]');
      const curMm = wrap.querySelector('[data-lux-select-current-mm]');
      const curBadge = wrap.querySelector('[data-lux-select-current-badge]');

      // Initialize line item property
      if (nativeSelect && nativeSelect.value) {
        if (nativeSelect.hasAttribute('data-lux-carat-select')) {
          ensurePropertyInput('Carat Weight', nativeSelect.value);
        } else {
          ensurePropertyInput('Ring Size', nativeSelect.value);
        }
      }

      if (!trigger || !dropdown) return;

      // Toggle dropdown open/close
      trigger.addEventListener('click', (e) => {
        e.stopPropagation();
        const isOpen = wrap.classList.toggle('is-open');
        trigger.setAttribute('aria-expanded', isOpen);

        // Close any other custom selects
        document.querySelectorAll('[data-lux-custom-select-wrap]').forEach((w) => {
          if (w !== wrap) {
            w.classList.remove('is-open');
            w.querySelector('[data-lux-custom-select-trigger]')?.setAttribute('aria-expanded', 'false');
          }
        });

        // Scroll selected item into view smoothly
        if (isOpen) {
          const selectedItem = wrap.querySelector('.lux-custom-select-item.is-selected');
          if (selectedItem) {
            dropdown.scrollTop = selectedItem.offsetTop - dropdown.offsetTop - 30;
          }
        }
      });

      // Item selection
      items.forEach((item) => {
        item.addEventListener('click', (e) => {
          e.stopPropagation();
          const val = item.getAttribute('data-val');
          const sizeText = item.getAttribute('data-size') || val;
          const mmText = item.getAttribute('data-mm') || '';
          const badgeText = item.getAttribute('data-badge') || '';

          // Update active item styling
          items.forEach((i) => {
            i.classList.remove('is-selected');
            i.setAttribute('aria-selected', 'false');
          });
          item.classList.add('is-selected');
          item.setAttribute('aria-selected', 'true');

          // Update trigger display
          if (curSize) curSize.textContent = sizeText;
          const caratDisplay = wrap.querySelector('[data-lux-carat-current-display]');
          if (caratDisplay) caratDisplay.textContent = sizeText;
          if (curMm) {
            curMm.textContent = mmText;
            curMm.style.display = mmText ? '' : 'none';
          }
          if (curBadge) {
            curBadge.textContent = badgeText;
            curBadge.style.display = badgeText ? '' : 'none';
          }

          // Close dropdown
          wrap.classList.remove('is-open');
          trigger.setAttribute('aria-expanded', 'false');

          // Sync with native select and trigger change
          if (nativeSelect) {
            nativeSelect.value = val;
            nativeSelect.dispatchEvent(new Event('change', { bubbles: true }));
          }

          const isCarat = nativeSelect?.hasAttribute('data-lux-carat-select') || item.hasAttribute('data-carat');
          if (isCarat) {
            ensurePropertyInput('Carat Weight', val);
            updateCaratSimulator(val);
            const optName = nativeSelect?.getAttribute('data-option-name');
            if (optName) {
              syncOptionValue(optName, val);
            }
            applyCustomizerToVariant();
          } else {
            // Ensure cart line item property for Ring Size
            ensurePropertyInput('Ring Size', val);
            const optName = nativeSelect?.getAttribute('data-option-name');
            if (optName) {
              syncOptionValue(optName, val);
            }
            applyCustomizerToVariant();
          }
        });
      });
    });

    // Close on click outside or Escape
    document.addEventListener('click', (e) => {
      if (!e.target.closest('[data-lux-custom-select-wrap]')) {
        document.querySelectorAll('[data-lux-custom-select-wrap].is-open').forEach((w) => {
          w.classList.remove('is-open');
          w.querySelector('[data-lux-custom-select-trigger]')?.setAttribute('aria-expanded', 'false');
        });
      }
    });

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        document.querySelectorAll('[data-lux-custom-select-wrap].is-open').forEach((w) => {
          w.classList.remove('is-open');
          w.querySelector('[data-lux-custom-select-trigger]')?.setAttribute('aria-expanded', 'false');
        });
      }
    });

    // 5. CARAT WEIGHT BUTTONS SYNC
    const caratBtns = document.querySelectorAll('[data-lux-carat-btn]');
    const caratLabel = document.querySelector('[data-selected-carat-name]');
    
    // Scale map for Diaels Diamond Hand Simulator
    const caratScaleMap = {
      '1.0': 0.565, '1.0 ct': 0.565,
      '1.5': 0.652, '1.5 ct': 0.652,
      '2.0': 0.696, '2.0 ct': 0.696,
      '2.5': 0.783, '2.5 ct': 0.783,
      '3.0': 0.826, '3.0 ct': 0.826,
      '3.5': 0.87, '3.5 ct': 0.87,
      '4.0': 0.913, '4.0 ct': 0.913,
      '4.5': 0.957, '4.5 ct': 0.957,
      '5.0': 1.0, '5.0 ct': 1.0
    };

    function updateCaratSimulator(cVal) {
      if (!cVal) return;
      const cClean = cVal.toLowerCase().replace('ct', '').trim();
      const dsgImg = document.querySelector('.dsg-diamond-img');
      if (dsgImg && caratScaleMap[cClean]) {
        dsgImg.style.transform = `translate(-50%, -50%) scale(${caratScaleMap[cClean]}) rotate(60deg)`;
      }
      const dsgBtns = document.querySelectorAll('.dsg-size-btn');
      dsgBtns.forEach((b) => {
        const isMatch = b.textContent.trim() === cClean;
        b.classList.toggle('active', isMatch);
        if (isMatch) {
          const activeBg = document.querySelector('.dsg-active-bg');
          if (activeBg) {
            activeBg.style.width = `${b.offsetWidth}px`;
            activeBg.style.height = `${b.offsetHeight}px`;
            activeBg.style.top = `${b.offsetTop}px`;
            activeBg.style.transform = `translateX(${b.offsetLeft}px)`;
          }
        }
      });
    }

    const initCaratBtn = document.querySelector('[data-lux-carat-btn].is-selected');
    if (initCaratBtn) {
      const initVal = initCaratBtn.getAttribute('data-val') || '2.0 CT';
      ensurePropertyInput('Carat Weight', initVal);
      setTimeout(() => updateCaratSimulator(initVal), 150);
    }

    const initCaratSelect = document.querySelector('[data-lux-carat-select]');
    if (initCaratSelect && initCaratSelect.value) {
      ensurePropertyInput('Carat Weight', initCaratSelect.value);
      setTimeout(() => updateCaratSimulator(initCaratSelect.value), 150);
      initCaratSelect.addEventListener('change', () => {
        const val = initCaratSelect.value;
        ensurePropertyInput('Carat Weight', val);
        updateCaratSimulator(val);
        const optName = initCaratSelect.getAttribute('data-option-name');
        if (optName) {
          syncOptionValue(optName, val);
        }
        const disp = document.querySelector('[data-lux-carat-current-display]');
        if (disp) disp.textContent = val;

        // Also sync top carat buttons if present
        caratBtns.forEach((b) => {
          const bVal = b.getAttribute('data-val');
          if (matchCaratValues(bVal, val)) {
            caratBtns.forEach((btn) => btn.classList.remove('is-selected'));
            b.classList.add('is-selected');
            if (caratLabel) caratLabel.textContent = bVal;
          }
        });

        applyCustomizerToVariant();
      });
    }

    // Buy It Now direct checkout handler
    const buyNowBtn = document.querySelector('[data-lux-buy-now]');
    if (buyNowBtn) {
      buyNowBtn.addEventListener('click', async (e) => {
        e.preventDefault();
        const formId = buyNowBtn.getAttribute('form');
        const form = formId ? document.getElementById(formId) : buyNowBtn.closest('form');
        if (!form) return;

        buyNowBtn.disabled = true;
        const btnText = buyNowBtn.querySelector('.lux-btn-text');
        if (btnText) btnText.textContent = 'CHECKING OUT...';

        try {
          const formData = new FormData(form);
          const res = await fetch('/cart/add.js', {
            method: 'POST',
            body: formData
          });
          if (res.ok) {
            window.location.href = '/checkout';
          } else {
            form.submit();
          }
        } catch (err) {
          window.location.href = '/checkout';
        }
      });
    }

    caratBtns.forEach((btn) => {
      btn.addEventListener('click', () => {
        const cVal = btn.getAttribute('data-val');
        const optName = btn.getAttribute('data-option-name');
        if (!cVal) return;

        caratBtns.forEach((b) => b.classList.remove('is-selected'));
        btn.classList.add('is-selected');
        if (caratLabel) caratLabel.textContent = cVal;

        if (optName) {
          syncOptionValue(optName, cVal);
        }
        ensurePropertyInput('Carat Weight', cVal);
        updateCaratSimulator(cVal);

        // Also sync top dropdown if present
        if (initCaratSelect) {
          const matchOpt = Array.from(initCaratSelect.options).find((opt) => matchCaratValues(opt.value, cVal));
          if (matchOpt) {
            initCaratSelect.value = matchOpt.value;
            const disp = document.querySelector('[data-lux-carat-current-display]');
            if (disp) disp.textContent = matchOpt.value;
          }
        }

        applyCustomizerToVariant();
      });
    });

    let allVariants = [];
    const variantsScript = document.querySelector('[data-lux-variants-json]');
    if (variantsScript) {
      try {
        allVariants = JSON.parse(variantsScript.textContent);
      } catch (e) {
        console.warn('Could not parse variants JSON', e);
      }
    }

    function matchCaratValues(valA, valB) {
      if (valA == null || valB == null) return false;
      const strA = String(valA).toLowerCase().trim();
      const strB = String(valB).toLowerCase().trim();
      if (strA === strB) return true;

      // Extract decimal numbers (e.g. "1.00 ct" -> 1.00, "1 CT" -> 1, "2.5" -> 2.5)
      const matchA = strA.match(/(\d+(?:\.\d+)?)/);
      const matchB = strB.match(/(\d+(?:\.\d+)?)/);
      if (matchA && matchB) {
        const numA = parseFloat(matchA[1]);
        const numB = parseFloat(matchB[1]);
        if (!isNaN(numA) && !isNaN(numB) && Math.abs(numA - numB) < 0.01) {
          return true;
        }
      }

      const cleanA = strA.replace(/[^a-z0-9]/g, '');
      const cleanB = strB.replace(/[^a-z0-9]/g, '');
      return cleanA === cleanB || cleanA.includes(cleanB) || cleanB.includes(cleanA);
    }

    function findMatchingVariant() {
      if (!allVariants || allVariants.length === 0) return null;

      const isNonGold = (currentMetal === 'Platinum' || currentMetal === 'Silver');
      const targetMetalClean = (currentMetal || '').toLowerCase();
      const targetPurityClean = (currentPurity || '').toLowerCase();

      const selectedSize = document.querySelector('[data-lux-size-select]')?.value?.toLowerCase().trim();
      const selectedCarat = document.querySelector('[data-lux-carat-select]')?.value?.toLowerCase().trim()
        || document.querySelector('[data-lux-carat-btn].is-selected')?.getAttribute('data-val')?.toLowerCase().trim()
        || document.querySelector('.dsg-size-btn.active')?.textContent?.toLowerCase().trim();

      let bestVariant = null;
      let bestScore = -1;

      allVariants.forEach((v) => {
        const vTitle = (v.title || '').toLowerCase();
        const vOptions = (v.options || []).map((o) => (o || '').toLowerCase().trim());
        const titleParts = vTitle.split('/').map((s) => s.trim());
        let score = 0;

        // 1. Metal & Purity matching
        if (isNonGold) {
          const metalTerm = targetMetalClean === 'platinum' ? 'plat' : 'silver';
          if (vTitle.includes(metalTerm) || vOptions.some((o) => o.includes(metalTerm))) {
            score += 30;
          } else {
            return; // Can't match gold variant
          }
        } else {
          // Gold: White Gold, Yellow Gold, Rose Gold
          const colorTerm = targetMetalClean.includes('yellow') ? 'yellow' : (targetMetalClean.includes('rose') ? 'rose' : 'white');
          const hasColor = vTitle.includes(colorTerm) || vOptions.some((o) => o.includes(colorTerm));
          if (!hasColor) return; // Wrong metal color

          score += 20;

          // Purity: 10K, 14K, 18K
          const hasPurity = vTitle.includes(targetPurityClean) || vOptions.some((o) => o.includes(targetPurityClean));
          if (hasPurity) {
            score += 25;
          } else {
            // If variant explicitly contains another gold purity, exclude it
            const otherPurities = ['10k', '14k', '18k'].filter((p) => p !== targetPurityClean);
            const hasOtherPurity = otherPurities.some((p) => vTitle.includes(p) || vOptions.some((o) => o.includes(p)));
            if (hasOtherPurity) return; // Wrong purity
          }
        }

        // 2. Carat / Diamond Size matching
        if (selectedCarat) {
          const caratMatch = vOptions.some((opt) => matchCaratValues(opt, selectedCarat))
            || titleParts.some((p) => matchCaratValues(p, selectedCarat));

          if (caratMatch) {
            score += 50;
          } else {
            // Check if variant has an option or title segment representing another carat/size
            const hasDifferentCarat = vOptions.some((opt) => {
              const hasNum = /\d+(\.\d+)?/.test(opt);
              const isMetalPurity = opt.includes('gold') || opt.includes('white') || opt.includes('yellow') || opt.includes('rose') || opt.includes('plat') || opt.includes('silver') || opt.includes('10k') || opt.includes('14k') || opt.includes('18k');
              return hasNum && !isMetalPurity && !matchCaratValues(opt, selectedCarat);
            }) || titleParts.some((p) => {
              const hasNum = /\d+(\.\d+)?/.test(p);
              const isMetalPurity = p.includes('gold') || p.includes('white') || p.includes('yellow') || p.includes('rose') || p.includes('plat') || p.includes('silver') || p.includes('10k') || p.includes('14k') || p.includes('18k');
              return hasNum && !isMetalPurity && !matchCaratValues(p, selectedCarat);
            });

            if (hasDifferentCarat) {
              return; // Wrong carat!
            }
          }
        }

        // 3. Ring Size matching (if variants differ by ring size)
        if (selectedSize) {
          const sizeClean = selectedSize.replace('us', '').trim();
          if (vOptions.includes(selectedSize) || vOptions.includes(sizeClean)) {
            score += 5;
          }
        }

        if (score > bestScore) {
          bestScore = score;
          bestVariant = v;
        }
      });

      return bestVariant || allVariants[0];
    }

    function applyCustomizerToVariant() {
      const matched = findMatchingVariant();
      if (!matched) return;

      // Update price display
      updatePricing(matched);

      // Update form hidden input variant id
      const forms = document.querySelectorAll('form[action*="/cart/add"], form[data-type="add-to-cart-form"]');
      forms.forEach((form) => {
        const idInput = form.querySelector('input[name="id"]');
        if (idInput) {
          idInput.value = matched.id;
        }
      });

      // Update Add to Cart button state
      if (luxAddToCartBtn) {
        if (!matched.available) {
          luxAddToCartBtn.textContent = 'SOLD OUT';
          luxAddToCartBtn.disabled = true;
          luxAddToCartBtn.setAttribute('aria-disabled', 'true');
        } else {
          luxAddToCartBtn.textContent = 'ADD TO CART';
          luxAddToCartBtn.disabled = false;
          luxAddToCartBtn.removeAttribute('aria-disabled');
        }
      }

      // Sync with Dawn native inputs (metal / purity)
      const isNonGold = (currentMetal === 'Platinum' || currentMetal === 'Silver');
      document.querySelectorAll('fieldset.product-form__input input[type="radio"]').forEach((radio) => {
        const rVal = (radio.value || '').toLowerCase().trim();
        const rFieldset = radio.closest('fieldset');
        const fLegend = rFieldset ? (rFieldset.querySelector('legend')?.textContent || '').toLowerCase().trim() : '';
        if (fLegend.includes('metal') || fLegend.includes('color') || fLegend.includes('material')) {
          if (isNonGold) {
            const term = currentMetal.toLowerCase() === 'platinum' ? 'plat' : 'silver';
            if (rVal.includes(term)) {
              radio.checked = true;
            }
          } else {
            const cTerm = currentMetal.toLowerCase().includes('yellow') ? 'yellow' : (currentMetal.toLowerCase().includes('rose') ? 'rose' : 'white');
            const pTerm = currentPurity.toLowerCase();
            if (rVal.includes(cTerm) && rVal.includes(pTerm)) {
              radio.checked = true;
            } else if (rVal.includes(cTerm)) {
              radio.checked = true;
            }
          }
        }
      });

      // Sync Dawn native inputs for Carat / Diamond Size
      const curCarat = document.querySelector('[data-lux-carat-select]')?.value
        || document.querySelector('[data-lux-carat-btn].is-selected')?.getAttribute('data-val')
        || document.querySelector('.dsg-size-btn.active')?.textContent?.trim();
      if (curCarat) {
        document.querySelectorAll('fieldset.product-form__input').forEach((f) => {
          const fLegend = (f.querySelector('legend')?.textContent || '').toLowerCase().trim();
          if (fLegend.includes('carat') || fLegend.includes('diamond') || fLegend.includes('stone') || fLegend.includes('size')) {
            f.querySelectorAll('input[type="radio"]').forEach((radio) => {
              if (matchCaratValues(radio.value, curCarat)) {
                if (!radio.checked) {
                  radio.checked = true;
                  radio.dispatchEvent(new Event('change', { bubbles: true }));
                }
              }
            });
          }
        });
      }
    }

    function syncOptionValue(targetOptName, targetVal) {
      const lowerVal = (targetVal || '').toLowerCase().trim();
      const lowerName = (targetOptName || '').toLowerCase().trim();
      const isCaratSearch = lowerName.includes('carat') || lowerName.includes('diamond') || lowerName.includes('stone') || lowerName.includes('ct');

      // Check Dawn's native radio inputs
      document.querySelectorAll('fieldset.product-form__input input[type="radio"]').forEach((radio) => {
        const rVal = (radio.value || '').toLowerCase().trim();
        const rFieldset = radio.closest('fieldset');
        const fLegend = rFieldset ? (rFieldset.querySelector('legend')?.textContent || '').toLowerCase().trim() : '';
        const isFieldsetCarat = fLegend.includes('carat') || fLegend.includes('diamond') || fLegend.includes('stone') || fLegend.includes('size');

        if (!lowerName || fLegend.includes(lowerName) || lowerName.includes(fLegend) || (isCaratSearch && isFieldsetCarat)) {
          if (rVal === lowerVal || matchCaratValues(rVal, lowerVal)) {
            radio.checked = true;
            radio.dispatchEvent(new Event('change', { bubbles: true }));
          }
        }
      });

      // Check Dawn's native select elements
      document.querySelectorAll('select.select__select, variant-selects select').forEach((sel) => {
        const selName = (sel.getAttribute('data-option-name') || sel.name || '').toLowerCase();
        const isSelCarat = selName.includes('carat') || selName.includes('diamond') || selName.includes('stone') || selName.includes('ct');
        if (!lowerName || selName.includes(lowerName) || lowerName.includes(selName) || (isCaratSearch && isSelCarat)) {
          Array.from(sel.options).forEach((opt) => {
            const oVal = (opt.value || '').toLowerCase().trim();
            if (oVal === lowerVal || matchCaratValues(opt.value, targetVal)) {
              sel.value = opt.value;
              sel.dispatchEvent(new Event('change', { bubbles: true }));
            }
          });
        }
      });

      // Update customizer variant
      applyCustomizerToVariant();
    }

    function ensurePropertyInput(name, value) {
      const forms = document.querySelectorAll('form[action*="/cart/add"], form[data-type="add-to-cart-form"]');
      forms.forEach((form) => {
        let input = form.querySelector(`input[name="properties[${name}]"]`);
        if (!input) {
          input = document.createElement('input');
          input.type = 'hidden';
          input.name = `properties[${name}]`;
          form.appendChild(input);
        }
        input.value = value;
      });
    }

    // 6. UPDATE "KNOW YOUR RING" METAL CARD ACCORDING TO SELECTION
    function updateMetalSpecCard(metalName) {
      const displayEl = document.getElementById('metal-name-display');
      const centerEl = document.getElementById('metal-karat-center');
      const pieEl = document.getElementById('lux-metal-donut-chart');
      const materialDetail = document.querySelector('[data-lux-material-detail]');

      if (displayEl) displayEl.textContent = metalName;
      if (materialDetail) materialDetail.textContent = metalName;

      const lower = metalName.toLowerCase();
      if (centerEl) {
        if (lower.includes('14k')) centerEl.textContent = '14K';
        else if (lower.includes('18k')) centerEl.textContent = '18K';
        else if (lower.includes('10k')) centerEl.textContent = '10K';
        else if (lower.includes('plat')) centerEl.textContent = 'PT';
        else if (lower.includes('silver')) centerEl.textContent = '925';
        else centerEl.textContent = 'GOLD';
      }

      if (pieEl) {
        if (lower.includes('rose')) {
          pieEl.style.background = 'conic-gradient(#df4c00 0% 48%, #f5c400 48% 89.7%, #9baaaa 89.7% 100%)';
        } else if (lower.includes('white') || lower.includes('silver')) {
          pieEl.style.background = 'conic-gradient(#e9eeee 0% 50%, #9baaaa 50% 85%, #df4c00 85% 100%)';
        } else if (lower.includes('plat')) {
          pieEl.style.background = 'conic-gradient(#9baaaa 0% 95%, #333333 95% 100%)';
        } else {
          pieEl.style.background = 'conic-gradient(#f5c400 0% 41.7%, #df4c00 41.7% 73.7%, #e9eeee 73.7% 89.7%, #9baaaa 89.7% 100%)';
        }
      }
    }

    // 7. COMPREHENSIVE RING SIZE GUIDE MODAL (HOW TO MEASURE & INTERACTIVE FINDER)
    const sizeData = [
      { us: 'US 3', uk: 'F', eu: '44', diam: 14.1, circ: 44.2, pop: false },
      { us: 'US 3.5', uk: 'G', eu: '45.5', diam: 14.5, circ: 45.5, pop: false },
      { us: 'US 4', uk: 'H', eu: '46.8', diam: 14.9, circ: 46.8, pop: false },
      { us: 'US 4.5', uk: 'I', eu: '48', diam: 15.3, circ: 48.0, pop: false },
      { us: 'US 5', uk: 'J 1/2', eu: '49.3', diam: 15.7, circ: 49.3, pop: false },
      { us: 'US 5.5', uk: 'K 1/2', eu: '50.6', diam: 16.1, circ: 50.6, pop: false },
      { us: 'US 6', uk: 'L 1/2', eu: '51.9', diam: 16.5, circ: 51.9, pop: true },
      { us: 'US 6.5', uk: 'M 1/2', eu: '53.1', diam: 16.9, circ: 53.1, pop: false },
      { us: 'US 7', uk: 'N 1/2', eu: '54.4', diam: 17.3, circ: 54.4, pop: true },
      { us: 'US 7.5', uk: 'O 1/2', eu: '55.7', diam: 17.7, circ: 55.7, pop: false },
      { us: 'US 8', uk: 'P 1/2', eu: '57.0', diam: 18.1, circ: 57.0, pop: false },
      { us: 'US 8.5', uk: 'Q 1/2', eu: '58.3', diam: 18.5, circ: 58.3, pop: false },
      { us: 'US 9', uk: 'R 1/2', eu: '59.5', diam: 18.9, circ: 59.5, pop: false },
      { us: 'US 9.5', uk: 'S 1/2', eu: '60.8', diam: 19.4, circ: 60.8, pop: false },
      { us: 'US 10', uk: 'T 1/2', eu: '62.1', diam: 19.8, circ: 62.1, pop: false },
      { us: 'US 10.5', uk: 'U 1/2', eu: '63.4', diam: 20.2, circ: 63.4, pop: false },
      { us: 'US 11', uk: 'V 1/2', eu: '64.6', diam: 20.6, circ: 64.6, pop: false },
      { us: 'US 11.5', uk: 'W 1/2', eu: '65.9', diam: 21.0, circ: 65.9, pop: false },
      { us: 'US 12', uk: 'X 1/2', eu: '67.2', diam: 21.4, circ: 67.2, pop: false }
    ];

    function applySelectedSizeToPdp(targetSizeVal) {
      if (!targetSizeVal) return;
      const cleanVal = targetSizeVal.trim();

      // Find matching size record
      const match = sizeData.find((s) => s.us.toLowerCase() === cleanVal.toLowerCase() || s.us.replace('US ', '').toLowerCase() === cleanVal.toLowerCase()) || sizeData[6];

      // Update Custom Dropdown Trigger
      document.querySelectorAll('[data-lux-custom-select-wrap]').forEach((wrap) => {
        const curSize = wrap.querySelector('[data-lux-select-current-size]');
        const curMm = wrap.querySelector('[data-lux-select-current-mm]');
        const curBadge = wrap.querySelector('[data-lux-select-current-badge]');
        const items = wrap.querySelectorAll('.lux-custom-select-item');
        const nativeSelect = wrap.querySelector('[data-lux-size-select]');

        if (curSize) curSize.textContent = match.us;
        if (curMm) curMm.textContent = `(${match.diam} mm)`;
        if (curBadge) {
          curBadge.style.display = match.pop ? '' : 'none';
        }

        items.forEach((item) => {
          const isSelected = item.getAttribute('data-val')?.toLowerCase() === match.us.toLowerCase();
          item.classList.toggle('is-selected', isSelected);
          item.setAttribute('aria-selected', isSelected ? 'true' : 'false');
        });

        if (nativeSelect) {
          nativeSelect.value = match.us;
          nativeSelect.dispatchEvent(new Event('change', { bubbles: true }));
        }
      });

      // Update cart line item property
      ensurePropertyInput('Ring Size', match.us);

      // Update table current pick buttons
      document.querySelectorAll('.lux-table-pick-btn').forEach((btn) => {
        const isPick = btn.getAttribute('data-pick-size')?.toLowerCase() === match.us.toLowerCase();
        btn.classList.toggle('is-current', isPick);
        btn.textContent = isPick ? 'Selected' : 'Select';
      });

      // Close modal
      const modal = document.getElementById('LuxumoonSizeGuideModal');
      if (modal) {
        modal.classList.remove('is-open');
        document.body.style.overflow = '';
      }
    }

    // Modal Open Event Delegation
    document.addEventListener('click', (e) => {
      const openTrigger = e.target.closest('[data-lux-size-guide-open], .lux-pdp-size-guide-trigger');
      if (openTrigger) {
        e.preventDefault();
        e.stopPropagation();
        const modal = document.getElementById('LuxumoonSizeGuideModal');
        if (modal) {
          modal.classList.add('is-open');
          document.body.style.overflow = 'hidden';
        }
        return;
      }

      // Modal Close Event Delegation
      const closeTrigger = e.target.closest('[data-lux-size-guide-close]');
      if (closeTrigger) {
        e.preventDefault();
        const modal = document.getElementById('LuxumoonSizeGuideModal');
        if (modal) {
          modal.classList.remove('is-open');
          document.body.style.overflow = '';
        }
        return;
      }

      // Modal Tab Switch Delegation
      const tabBtn = e.target.closest('[data-lux-modal-tab]');
      if (tabBtn) {
        e.preventDefault();
        const tabKey = tabBtn.getAttribute('data-lux-modal-tab');
        document.querySelectorAll('[data-lux-modal-tab]').forEach((b) => b.classList.toggle('active', b === tabBtn));
        document.querySelectorAll('.lux-modal-tab-content').forEach((content) => {
          content.classList.toggle('active', content.id === `lux-tab-content-${tabKey}`);
        });
        return;
      }

      // Table Direct Select Delegation
      const tablePick = e.target.closest('[data-pick-size]');
      if (tablePick) {
        e.preventDefault();
        const sVal = tablePick.getAttribute('data-pick-size');
        applySelectedSizeToPdp(sVal);
        return;
      }

      // Apply Button from Calculator
      const applyBtn = e.target.closest('#luxCalcApplyBtn');
      if (applyBtn) {
        e.preventDefault();
        const sVal = applyBtn.getAttribute('data-size-val');
        applySelectedSizeToPdp(sVal);
        return;
      }
    });

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        const modal = document.getElementById('LuxumoonSizeGuideModal');
        if (modal && modal.classList.contains('is-open')) {
          modal.classList.remove('is-open');
          document.body.style.overflow = '';
        }
      }
    });

    // Interactive Size Calculator Engine
    let calcMode = 'circ'; // 'circ' or 'diam'
    const calcToggleBtns = document.querySelectorAll('[data-calc-mode]');
    const calcSlider = document.getElementById('luxCalcRangeInput');
    const calcNumInput = document.getElementById('luxCalcNumberInput');
    const calcModeLabel = document.getElementById('luxCalcModeLabel');

    function updateCalculatorResult(val) {
      const num = parseFloat(val);
      if (isNaN(num)) return;

      let closestRecord = sizeData[6]; // default US 6
      let minDiff = Infinity;

      sizeData.forEach((record) => {
        const targetVal = calcMode === 'circ' ? record.circ : record.diam;
        const diff = Math.abs(num - targetVal);
        if (diff < minDiff) {
          minDiff = diff;
          closestRecord = record;
        }
      });

      // Update DOM Result Elements
      const resUs = document.getElementById('luxCalcResultUs');
      const resUk = document.getElementById('luxCalcResultUk');
      const resEu = document.getElementById('luxCalcResultEu');
      const resDiam = document.getElementById('luxCalcResultDiam');
      const resCirc = document.getElementById('luxCalcResultCirc');
      const resPop = document.getElementById('luxCalcPopBadge');
      const applyBtn = document.getElementById('luxCalcApplyBtn');

      if (resUs) resUs.textContent = closestRecord.us;
      if (resUk) resUk.textContent = closestRecord.uk;
      if (resEu) resEu.textContent = closestRecord.eu;
      if (resDiam) resDiam.textContent = `${closestRecord.diam} mm`;
      if (resCirc) resCirc.textContent = `${closestRecord.circ} mm`;
      if (resPop) resPop.style.display = closestRecord.pop ? '' : 'none';
      if (applyBtn) {
        applyBtn.setAttribute('data-size-val', closestRecord.us);
        applyBtn.textContent = `Select ${closestRecord.us} for this Ring & Close`;
      }
    }

    if (calcSlider && calcNumInput) {
      calcSlider.addEventListener('input', () => {
        calcNumInput.value = calcSlider.value;
        updateCalculatorResult(calcSlider.value);
      });

      calcNumInput.addEventListener('input', () => {
        calcSlider.value = calcNumInput.value;
        updateCalculatorResult(calcNumInput.value);
      });

      calcToggleBtns.forEach((btn) => {
        btn.addEventListener('click', () => {
          calcMode = btn.getAttribute('data-calc-mode');
          calcToggleBtns.forEach((b) => b.classList.toggle('active', b === btn));

          if (calcMode === 'circ') {
            if (calcModeLabel) calcModeLabel.textContent = 'Finger Circumference:';
            calcSlider.min = '44.0';
            calcSlider.max = '68.0';
            calcSlider.value = '51.9';
            calcNumInput.value = '51.9';
          } else {
            if (calcModeLabel) calcModeLabel.textContent = 'Ring Inside Diameter:';
            calcSlider.min = '14.0';
            calcSlider.max = '21.6';
            calcSlider.value = '16.5';
            calcNumInput.value = '16.5';
          }
          updateCalculatorResult(calcSlider.value);
        });
      });
    }

    // Size Table Live Filter
    const chartFilterInput = document.getElementById('luxSizeChartFilter');
    if (chartFilterInput) {
      chartFilterInput.addEventListener('input', () => {
        const query = chartFilterInput.value.toLowerCase().trim();
        const rows = document.querySelectorAll('#luxSizeTable tbody tr');
        rows.forEach((row) => {
          const rowText = row.textContent.toLowerCase();
          row.style.display = !query || rowText.includes(query) ? '' : 'none';
        });
      });
    }

    // 8. ADD ENGRAVING LIVE PREVIEW
    const engravingInput = document.querySelector('[data-lux-engraving-input]');
    const engravingCount = document.querySelector('[data-lux-engraving-count]');
    const fontSelect = document.querySelector('[data-lux-engraving-font]');
    const textPath = document.querySelector('[data-lux-engraving-text-path]');

    const fontMap = {
      'Cormorant Garamond': 'Georgia, "Times New Roman", serif',
      'Modern Block': 'Arial, Helvetica, sans-serif',
      'Classic Script': '"Brush Script MT", "Segoe Script", cursive',
      'Simple Type': '"Courier New", Courier, monospace'
    };

    function updateEngraving() {
      if (!engravingInput) return;
      const val = engravingInput.value.slice(0, 15);
      engravingInput.value = val;

      if (engravingCount) {
        engravingCount.textContent = val.length;
      }

      if (textPath) {
        textPath.textContent = val;
        const selectedFont = fontSelect ? fontSelect.value : 'Cormorant Garamond';
        textPath.setAttribute('font-family', fontMap[selectedFont] || 'Georgia, serif');
        textPath.setAttribute('font-style', selectedFont === 'Classic Script' ? 'italic' : 'normal');
        textPath.setAttribute('font-weight', selectedFont === 'Modern Block' ? '600' : '400');
      }

      if (val.trim()) {
        ensurePropertyInput('Engraving', val);
        ensurePropertyInput('Engraving Font', fontSelect ? fontSelect.value : 'Elegant Serif');
      }
    }

    if (engravingInput) {
      engravingInput.addEventListener('input', updateEngraving);
    }
    if (fontSelect) {
      fontSelect.addEventListener('change', updateEngraving);
    }

    // 9. TABS NAVIGATION
    const tabBtns = document.querySelectorAll('[data-lux-tab-btn]');
    const tabPanels = document.querySelectorAll('[data-lux-tab-panel]');

    tabBtns.forEach((btn) => {
      btn.addEventListener('click', () => {
        const targetId = btn.getAttribute('data-lux-tab-btn');

        tabBtns.forEach((b) => b.classList.toggle('active', b === btn));
        tabPanels.forEach((p) => {
          p.classList.toggle('active', p.getAttribute('data-lux-tab-panel') === targetId);
        });
      });
    });

    // 10. WISHLIST TOGGLE
    const wishlistBtn = document.querySelector('[data-lux-wishlist-toggle]');
    if (wishlistBtn && window.LucentWishlist) {
      const pId = wishlistBtn.getAttribute('data-product-id');
      if (window.LucentWishlist.has(pId)) {
        wishlistBtn.classList.add('is-active');
        const textSpan = wishlistBtn.querySelector('.lux-pdp-wishlist-text');
        if (textSpan) textSpan.textContent = 'Saved in Wishlist';
      }

      wishlistBtn.addEventListener('click', (e) => {
        e.preventDefault();
        const pData = {
          id: pId,
          handle: wishlistBtn.getAttribute('data-product-handle') || '',
          title: wishlistBtn.getAttribute('data-product-title') || document.title,
          price: wishlistBtn.getAttribute('data-product-price') || '',
          compare_at_price: wishlistBtn.getAttribute('data-product-compare-price') || '',
          image: wishlistBtn.getAttribute('data-product-image') || '',
          url: window.location.pathname,
          variantId: wishlistBtn.getAttribute('data-variant-id') || ''
        };

        const added = window.LucentWishlist.toggle(pData);
        wishlistBtn.classList.toggle('is-active', added);
        const textSpan = wishlistBtn.querySelector('.lux-pdp-wishlist-text');
        if (textSpan) {
          textSpan.textContent = added ? 'Saved in Wishlist' : 'Add to wish list';
        }
      });
    }

    // 11. ADD TO CART FEEDBACK & SUBMISSION
    const luxAddToCartBtn = document.getElementById('LuxAddToCartButton');
    if (luxAddToCartBtn) {
      luxAddToCartBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();

        if (luxAddToCartBtn.getAttribute('aria-disabled') === 'true' || luxAddToCartBtn.disabled) return;

        // 1. Sync Ring Size and Engraving properties to all forms
        if (sizeSelect && sizeSelect.value) {
          ensurePropertyInput('Ring Size', sizeSelect.value);
        }
        if (engravingInput && engravingInput.value.trim()) {
          ensurePropertyInput('Engraving', engravingInput.value.trim());
          ensurePropertyInput('Engraving Font', fontSelect ? fontSelect.value : 'Elegant Serif');
        }

        // 2. Locate main product form
        const formId = luxAddToCartBtn.getAttribute('form');
        let form = formId ? document.getElementById(formId) : null;
        if (!form) {
          form = document.querySelector('product-form form[data-type="add-to-cart-form"]') || document.querySelector('form[action*="/cart/add"]');
        }

        if (!form) {
          console.error('Add to Cart: product form not found');
          return;
        }

        const textEl = luxAddToCartBtn.querySelector('.lux-btn-text') || luxAddToCartBtn;
        const originalText = textEl.textContent.trim();
        luxAddToCartBtn.setAttribute('aria-disabled', 'true');
        textEl.textContent = 'ADDING...';

        const resetBtn = () => {
          luxAddToCartBtn.removeAttribute('aria-disabled');
          textEl.textContent = originalText;
        };

        // 3. Trigger Dawn's native product-form submit button
        const nativeSubmitBtn = form.querySelector('.product-form__submit') || form.querySelector('[type="submit"]');
        if (nativeSubmitBtn) {
          nativeSubmitBtn.click();
          setTimeout(resetBtn, 1500);
          return;
        }

        // 4. Fallback: Direct AJAX add to cart if native button absent
        const formData = new FormData(form);
        const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        if (cart) {
          formData.append(
            'sections',
            cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          cart.setActiveElement(document.activeElement);
        }

        fetch(window.routes ? window.routes.cart_add_url : '/cart/add.js', {
          method: 'POST',
          headers: {
            'X-Requested-With': 'XMLHttpRequest',
            'Accept': 'application/javascript'
          },
          body: formData
        })
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              const errorWrapper = form.querySelector('.product-form__error-message-wrapper');
              const errorMessage = form.querySelector('.product-form__error-message');
              if (errorWrapper && errorMessage) {
                errorMessage.textContent = response.description || response.message || 'Could not add to bag';
                errorWrapper.removeAttribute('hidden');
              } else {
                luxAddToCartBtn.textContent = response.description || response.message || 'Could not add to bag';
                setTimeout(resetBtn, 3000);
              }
            } else if (cart) {
              cart.renderContents(response);
            } else {
              window.location.href = window.routes ? window.routes.cart_url : '/cart';
            }
          })
          .catch((err) => {
            console.error('Error adding to cart:', err);
            form.submit();
          })
          .finally(() => {
            resetBtn();
          });
      });
    }

    // 12. DYNAMIC PRICING ENGINE WITH CASINO SLOT / ODOMETER ROLLING DIGITS MICRO-ANIMATION
    function animateOdometerPrice(el, newPriceText, isInitial) {
      if (!el || !newPriceText) return;
      const cleanNew = newPriceText.trim();
      const currentVal = el.getAttribute('data-price-val') || el.textContent.trim();
      if (currentVal === cleanNew && el.querySelector('.lux-odometer-wrap')) return;

      el.setAttribute('data-price-val', cleanNew);
      el.setAttribute('aria-label', cleanNew);

      // Flash micro-interaction glow only on actual changes
      if (!isInitial) {
        el.classList.remove('lux-odometer-flash');
        void el.offsetWidth; // reflow
        el.classList.add('lux-odometer-flash');
      }

      const chars = cleanNew.split('');
      const existingWrap = el.querySelector('.lux-odometer-wrap');

      // FAST PATH: Seamless in-place roll if wrap exists and structure matches (prevents any 0 flash)
      if (existingWrap && existingWrap.children.length === chars.length) {
        let canReuse = true;
        for (let i = 0; i < chars.length; i++) {
          const child = existingWrap.children[i];
          const isDigitChar = /\d/.test(chars[i]);
          const isDigitNode = child.classList.contains('lux-odometer-digit');
          if (isDigitChar !== isDigitNode) {
            canReuse = false;
            break;
          }
        }

        if (canReuse) {
          chars.forEach((char, idx) => {
            const child = existingWrap.children[idx];
            if (/\d/.test(char)) {
              const digit = parseInt(char, 10);
              const ribbon = child.querySelector('.lux-odometer-ribbon');
              if (ribbon) {
                ribbon.style.transition = 'transform 0.65s cubic-bezier(0.16, 1, 0.3, 1)';
                ribbon.style.transitionDelay = `${idx * 25}ms`;
                ribbon.style.transform = `translateY(-${digit * 10}%)`;
              }
            } else {
              child.textContent = char;
            }
          });
          return;
        }
      }

      // FULL PATH: Initial render or structure length change
      const oldDigits = (currentVal.match(/\d/g) || []);
      const newDigits = (cleanNew.match(/\d/g) || []);
      const wrap = document.createElement('span');
      wrap.className = 'lux-odometer-wrap';

      let digitCounter = 0;
      const ribbonsToAnimate = [];

      chars.forEach((char, idx) => {
        if (/\d/.test(char)) {
          const digit = parseInt(char, 10);
          const digitBox = document.createElement('span');
          digitBox.className = 'lux-odometer-digit';

          const ribbon = document.createElement('span');
          ribbon.className = 'lux-odometer-ribbon';

          // Align by place-value from the right (cents, units, tens, hundreds...)
          const distFromRight = (newDigits.length - 1) - digitCounter;
          digitCounter++;
          const oldDigitChar = oldDigits[oldDigits.length - 1 - distFromRight];
          const startDigit = (!isInitial && oldDigitChar !== undefined)
            ? parseInt(oldDigitChar, 10)
            : digit; // Start directly at target digit if initial or new place-value (NEVER reset to 0)

          for (let i = 0; i <= 9; i++) {
            const numSpan = document.createElement('span');
            numSpan.textContent = String(i);
            ribbon.appendChild(numSpan);
          }

          if (isInitial || startDigit === digit) {
            ribbon.style.transition = 'none';
            ribbon.style.transform = `translateY(-${digit * 10}%)`;
          } else {
            ribbon.style.transition = 'none';
            ribbon.style.transform = `translateY(-${startDigit * 10}%)`;
            ribbonsToAnimate.push({ ribbon, targetDigit: digit, delay: idx * 25 });
          }

          digitBox.appendChild(ribbon);
          wrap.appendChild(digitBox);
        } else {
          const staticBox = document.createElement('span');
          staticBox.className = 'lux-odometer-static';
          staticBox.textContent = char;
          wrap.appendChild(staticBox);
        }
      });

      el.innerHTML = '';
      el.appendChild(wrap);

      if (ribbonsToAnimate.length > 0) {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            ribbonsToAnimate.forEach(({ ribbon, targetDigit, delay }) => {
              ribbon.style.transition = 'transform 0.65s cubic-bezier(0.16, 1, 0.3, 1)';
              ribbon.style.transitionDelay = `${delay}ms`;
              ribbon.style.transform = `translateY(-${targetDigit * 10}%)`;
            });
          });
        });
      }
    }

    function updatePricing(variant, html) {
      if (!variant) return;

      const formatPrice = (cents) => {
        if (!cents && cents !== 0) return '';
        const sampleEl = document.querySelector('[data-lux-current-price]');
        const sampleText = sampleEl ? sampleEl.textContent.trim() : '';
        const currPrefix = (sampleText.match(/^[^\d]+/)?.[0]) || '$';
        return currPrefix + (Number(cents) / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      };

      let currentFormatted = formatPrice(variant.price);
      let compareFormatted = variant.compare_at_price && variant.compare_at_price > variant.price ? formatPrice(variant.compare_at_price) : null;

      // Calculate discount percentage
      let discountPct = 0;
      if (variant.compare_at_price && variant.compare_at_price > variant.price) {
        discountPct = Math.round(((variant.compare_at_price - variant.price) / variant.compare_at_price) * 100);
      }

      // Update active price elements with rolling digits
      document.querySelectorAll('[data-lux-current-price]').forEach((el) => {
        if (currentFormatted) {
          animateOdometerPrice(el, currentFormatted);
        }
      });

      // Update compare-at price elements
      document.querySelectorAll('[data-lux-compare-price]').forEach((el) => {
        if (compareFormatted && discountPct > 0) {
          el.textContent = compareFormatted;
          el.style.display = '';
        } else {
          el.style.display = 'none';
        }
      });

      // Update discount percentage badge
      document.querySelectorAll('[data-lux-discount-badge]').forEach((el) => {
        if (discountPct > 0) {
          el.textContent = `${discountPct}% OFF`;
          el.style.display = '';
        } else {
          el.style.display = 'none';
        }
      });
    }

    // Initialize price odometer structure immediately on page load so ribbons start ready
    document.querySelectorAll('[data-lux-current-price]').forEach((el) => {
      const text = el.textContent.trim();
      if (text) {
        animateOdometerPrice(el, text, true);
      }
    });

    // 13. SUBSCRIBE TO VARIANT CHANGE & CART UPDATE EVENTS
    if (typeof subscribe !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
        if (!event || !event.data || !event.data.variant) return;
        const variant = event.data.variant;
        const html = event.data.html;

        // Dynamically update pricing across PDP
        updatePricing(variant, html);

        if (luxAddToCartBtn) {
          const textEl = luxAddToCartBtn.querySelector('.lux-btn-text') || luxAddToCartBtn;
          if (!variant.available) {
            textEl.textContent = 'SOLD OUT';
            luxAddToCartBtn.disabled = true;
          } else {
            textEl.textContent = 'ADD TO CART';
            luxAddToCartBtn.disabled = false;
          }
        }
      });

      subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
        if (luxAddToCartBtn) {
          luxAddToCartBtn.removeAttribute('aria-disabled');
          const textEl = luxAddToCartBtn.querySelector('.lux-btn-text') || luxAddToCartBtn;
          textEl.textContent = 'ADD TO CART';
        }
      });
    }

    // 14. DIAELS DIAMOND CARAT SIZE GUIDE INTERACTIVITY
    document.querySelectorAll('.dsg-wrapper').forEach((wrapper) => {
      const diamondImg = wrapper.querySelector('.dsg-diamond-img');
      const buttons = wrapper.querySelectorAll('.dsg-size-btn');
      const activeBg = wrapper.querySelector('.dsg-active-bg');

      function moveBackground(targetBtn) {
        if (!targetBtn || !activeBg) return;
        activeBg.style.width = `${targetBtn.offsetWidth}px`;
        activeBg.style.height = `${targetBtn.offsetHeight}px`;
        activeBg.style.top = `${targetBtn.offsetTop}px`;
        activeBg.style.transform = `translateX(${targetBtn.offsetLeft}px)`;
        activeBg.style.opacity = '1';
      }

      const startBtn = wrapper.querySelector('.dsg-size-btn.active') || buttons[0];
      if (startBtn) {
        requestAnimationFrame(() => moveBackground(startBtn));
        setTimeout(() => moveBackground(startBtn), 60);
        setTimeout(() => moveBackground(startBtn), 250);
        window.addEventListener('load', () => moveBackground(startBtn));
      }

      buttons.forEach((btn) => {
        btn.addEventListener('click', function (e) {
          e.preventDefault();
          buttons.forEach((b) => b.classList.remove('active'));
          this.classList.add('active');
          moveBackground(this);
          if (diamondImg) {
            const scale = this.getAttribute('data-scale') || '0.826';
            diamondImg.style.transform = `translate(-50%, -50%) scale(${scale}) rotate(60deg)`;
          }

          // Two-way sync to top Carat Weight buttons & dropdown
          const btnVal = this.textContent.trim();
          ensurePropertyInput('Carat Weight', btnVal);
          updateCaratSimulator(btnVal);

          // 1. Sync to top Carat buttons if present
          const matchedTopBtn = Array.from(document.querySelectorAll('[data-lux-carat-btn]')).find((b) => {
            const v = b.getAttribute('data-val') || '';
            return matchCaratValues(v, btnVal);
          });
          if (matchedTopBtn) {
            document.querySelectorAll('[data-lux-carat-btn]').forEach((b) => b.classList.remove('is-selected'));
            matchedTopBtn.classList.add('is-selected');
            const cLabel = document.querySelector('[data-selected-carat-name]');
            if (cLabel) cLabel.textContent = matchedTopBtn.getAttribute('data-val');
          }

          // 2. Sync to top Diamond Size dropdown if present
          const caratSelect = document.querySelector('[data-lux-carat-select]');
          if (caratSelect) {
            const matchedOpt = Array.from(caratSelect.options).find((opt) => matchCaratValues(opt.value, btnVal));
            if (matchedOpt) {
              caratSelect.value = matchedOpt.value;
              const disp = document.querySelector('[data-lux-carat-current-display]');
              if (disp) disp.textContent = matchedOpt.value;
              const optName = caratSelect.getAttribute('data-option-name');
              if (optName) syncOptionValue(optName, matchedOpt.value);
            }
          }

          // 3. Update variant matching and live price odometer
          applyCustomizerToVariant();
        });
      });

      // Continuous accurate alignment across all viewport and container resizes
      if (window.ResizeObserver) {
        const ro = new ResizeObserver(() => {
          const current = wrapper.querySelector('.dsg-size-btn.active') || buttons[0];
          if (current) moveBackground(current);
        });
        const track = wrapper.querySelector('.dsg-buttons-track');
        if (track) ro.observe(track);
      }

      window.addEventListener('resize', () => {
        const current = wrapper.querySelector('.dsg-size-btn.active') || buttons[0];
        if (activeBg && current) {
          activeBg.style.transition = 'none';
          moveBackground(current);
          setTimeout(() => {
            activeBg.style.transition = 'transform 0.4s cubic-bezier(0.25, 1, 0.5, 1), width 0.25s ease';
          }, 100);
        }
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initLuxumoonPDP);
  } else {
    initLuxumoonPDP();
  }
})();
